<template>
  <div>
    <!-- Overlay -->
    <transition name="overlay">
      <div
        v-if="modelValue"
        class="nav-drawer-overlay"
        @click="close"
      ></div>
    </transition>

    <!-- Drawer -->
    <transition name="drawer">
      <aside
        v-show="modelValue"
        :class="drawerClasses"
        :style="drawerStyle"
      >
        <div v-if="$slots.header" class="nav-drawer__header">
          <slot name="header" />
        </div>

        <nav class="nav-drawer__content">
          <slot />
        </nav>

        <div v-if="$slots.footer" class="nav-drawer__footer">
          <slot name="footer" />
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'NavDrawer',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    width: {
      type: [String, Number],
      default: 280
    },
    position: {
      type: String,
      default: 'left',
      validator: (v) => ['left', 'right'].includes(v)
    },
    persistent: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  computed: {
    drawerClasses() {
      return [
        'nav-drawer',
        `nav-drawer--${this.position}`
      ]
    },
    drawerStyle() {
      const width = typeof this.width === 'number' ? `${this.width}px` : this.width
      return { width }
    }
  },
  watch: {
    modelValue(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
        // 드로어가 열릴 때 현재 페이지 메뉴를 화면 안으로 스크롤해 위치를 바로 보이게 한다.
        this.$nextTick(() => {
          try {
            const el = this.$el && this.$el.querySelector('.nav-drawer__content [aria-current="page"]')
            if (el && typeof el.scrollIntoView === 'function') {
              el.scrollIntoView({ block: 'center' })
            }
          } catch (e) { /* no-op */ }
        })
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  },
  methods: {
    close() {
      if (!this.persistent) {
        this.$emit('update:modelValue', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.nav-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: $z-index-drawer;
}

.nav-drawer {
  position: fixed;
  top: 0;
  bottom: 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
  z-index: $z-index-drawer + 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 85vw;

  &--left {
    left: 0;
    border-right: none;
  }

  &--right {
    right: 0;
    border-left: none;
  }

  &__header {
    padding: $spacing-3 $spacing-3;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;
    border-bottom: none;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: $spacing-2 0;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
    }
  }

  &__footer {
    padding: $spacing-4;
    border-top: 1px solid #e2e8f0;
    background-color: #f8fafc;
  }
}

// Overlay animation
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity $transition-normal;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

// Drawer animation
.drawer-enter-active,
.drawer-leave-active {
  transition: transform $transition-normal $transition-timing;
}

.nav-drawer--left.drawer-enter-from,
.nav-drawer--left.drawer-leave-to {
  transform: translateX(-100%);
}

.nav-drawer--right.drawer-enter-from,
.nav-drawer--right.drawer-leave-to {
  transform: translateX(100%);
}
</style>
